import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, getDocs, getDoc, addDoc, query, where, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import ClienteChatV4 from './ClienteChatV4';
import ClienteProjetoScreen from './ClienteProjetoScreen';
import SinoNotificacoes from './SinoNotificacoes';
import { usePermissoes } from '../hooks/usePermissoes';

const STATUS_CONFIG = {
  analyzing:       { label: 'Em analise',           color: '#FFA726', bg: 'rgba(255,167,38,0.1)' },
  pendingApproval: { label: 'Orcamento disponivel', color: '#0080FF', bg: 'rgba(0,128,255,0.1)' },
  approved:        { label: 'Aprovado',             color: '#00E5C4', bg: 'rgba(0,229,196,0.1)' },
  inProgress:      { label: 'Em andamento',         color: '#0080FF', bg: 'rgba(0,128,255,0.1)' },
  completed:       { label: 'Concluido',            color: '#66BB6A', bg: 'rgba(102,187,106,0.1)' },
  rejected:        { label: 'Cancelado',            color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
};

export default function ClienteHome({ userData, onLogout, tenant }) {
  const { pode } = usePermissoes(userData);
  const [events, setEvents] = useState([]);
  const [tenantData, setTenantData] = useState(tenant || null);
  const [verbaBucket, setVerbaBucket] = useState(null); // { saldoVerba, periodoUso } — da unidade, ou da matriz se não tiver unidade

  // A verba não fica mais na pessoa — fica na Unidade dela, ou na Empresa
  // (matriz) se ela não tiver unidade. Busca o bucket certo aqui.
  useEffect(() => {
    const tid = userData?.tenantId;
    if (!tid) { setVerbaBucket(null); return; }
    const ref = userData?.unidadeId
      ? doc(db, 'tenants', tid, 'unidades', userData.unidadeId)
      : doc(db, 'tenants', tid);
    getDoc(ref).then(snap => {
      if (snap.exists()) setVerbaBucket({ saldoVerba: snap.data().saldoVerba || 0, periodoUso: snap.data().periodoUso || '' });
    }).catch(err => console.error('Erro ao buscar verba:', err));
  }, [userData?.tenantId, userData?.unidadeId]);

  // Se o tenant veio só com o ID (ex: franqueado acessou pelo domínio
  // principal, não pelo subdomínio da empresa), busca os dados completos
  // (logo, nome, cores) direto no Firestore — mesma correção já feita no
  // TenantAdmin.js.
  useEffect(() => {
    if (tenant?.nome) { setTenantData(tenant); return; }
    if (tenant?.id) {
      getDoc(doc(db, 'tenants', tenant.id)).then(snap => {
        if (snap.exists()) setTenantData({ id: snap.id, ...snap.data() });
      }).catch(e => console.error('Erro ao buscar tenant completo:', e));
    } else {
      setTenantData(null);
    }
  }, [tenant?.id, tenant?.nome]);

  const [activeSection, setActiveSection] = useState('workspace');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatKey, setChatKey] = useState(0);
  const [showChat, setShowChat] = useState(false);
  const [aprovando, setAprovando] = useState(false);
  const [tasksPendentesAprov, setTasksPendentesAprov] = useState([]);
  const [aprovandoTask, setAprovandoTask] = useState(false);

  // Histórico = eventos já encerrados (concluídos ou cancelados);
  // Workspace mostra só os que ainda estão em andamento.
  const eventosHistorico = events.filter(e => e.status === 'completed' || e.status === 'rejected');
  const eventosAtivos    = events.filter(e => e.status !== 'completed' && e.status !== 'rejected');

  const formatBRL = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // Verba do franqueado — mesma lógica de Alocado/Utilizado/Livre do TenantAdmin.js
  const saldoVerba = verbaBucket?.saldoVerba || 0;
  const valorAlocado = events.filter(e => ['pendingApproval', 'approved', 'analyzing'].includes(e.status))
    .reduce((acc, e) => acc + (e.orcamentoFinal?.total || e.financeiro?.valorTotal || 0), 0);
  const valorUtilizado = events.filter(e => e.status === 'completed')
    .reduce((acc, e) => acc + (e.orcamentoFinal?.total || e.financeiro?.valorTotal || 0), 0);
  const valorLivre = Math.max(0, saldoVerba - valorAlocado - valorUtilizado);
  const pctAlocado = saldoVerba > 0 ? Math.min(100, (valorAlocado / saldoVerba) * 100) : 0;
  const pctUtilizado = saldoVerba > 0 ? Math.min(100, (valorUtilizado / saldoVerba) * 100) : 0;

  const userId = userData?.uid || userData?.id;

  // Listener para abrir projeto via notificação do sino
  useEffect(() => {
    const handler = (e) => {
      const { budgetId } = e.detail || {};
      if (!budgetId) return;
      setEvents(prev => {
        const ev = prev.find(ev => ev.id === budgetId);
        if (ev) setSelectedEvent(ev);
        return prev;
      });
    };
    window.addEventListener('abrirProjeto', handler);
    return () => window.removeEventListener('abrirProjeto', handler);
  }, []);
  const userName = userData?.name || userData?.email?.split('@')[0] || 'Cliente';
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  useEffect(() => {
    if (!userId) return;
    const unsub = onSnapshot(
      query(collection(db, 'budgets'), where('clientUserId', '==', userId), where('isMae', '==', true)),
      snap => {
        setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return () => unsub();
  }, [userId]);

    // Busca tasks pendentes de aprovação do cliente
  useEffect(() => {
    if (!userId) return;
    const statusPendentes = ['aguardando_pre_aprovacao', 'aguardando_aprovacao_execucao', 'aguardando_aprovacao_entrega'];
    // Busca budgets do cliente para filtrar tasks
    const budgetIds = events.map(e => e.id);
    if (budgetIds.length === 0) return;
    const unsub = onSnapshot(
      query(collection(db, 'tasks'), where('budgetId', 'in', budgetIds.slice(0, 10))),
      snap => {
        const pendentes = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(t => statusPendentes.includes(t.status));
        setTasksPendentesAprov(pendentes);
      }
    );
    return () => unsub();
  }, [userId, events]);

  const handleAprovarTask = async (task, aprovado) => {
    if (!aprovado && !window.confirm('Solicitar ajuste nesta entrega?')) return;
    setAprovandoTask(true);
    try {
      await updateDoc(doc(db, 'tasks', task.id), {
        status: aprovado ? 'concluido' : 'ajuste',
        aprovacaoClienteEm: serverTimestamp(),
        aprovacaoClienteOk: aprovado,
        updatedAt: serverTimestamp(),
      });

      // Se aprovada e era task de EXECUÇÃO → verifica se todas concluíram para mudar para Acontecendo
      if (aprovado && task.status === 'aguardando_aprovacao_execucao') {
        try {
          const allTasksSnap = await getDocs(
            query(collection(db, 'tasks'), where('budgetId', '==', task.budgetId))
          );
          const allTasks = allTasksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          const tasksExec = allTasks.filter(t => t.fase === 'execucao');
          const todasExecConcluidas = tasksExec.every(t =>
            t.id === task.id ? true : t.status === 'concluido'
          );
          if (todasExecConcluidas) {
            await updateDoc(doc(db, 'budgets', task.budgetId), {
              workspaceStage: 'Acontecendo',
              status:         'inProgress',
              updatedAt:      serverTimestamp(),
            });
          }
        } catch (e) { console.error('Erro ao verificar Acontecendo:', e); }
      }

      // Se aprovada e era task de ENTREGA → verifica se todas concluíram para fechar o budget
      if (aprovado && task.status === 'aguardando_aprovacao_entrega') {
        try {
          const allTasksSnap = await getDocs(
            query(collection(db, 'tasks'), where('budgetId', '==', task.budgetId))
          );
          const allTasks = allTasksSnap.docs.map(d => ({ id: d.id, ...d.data() }));

          // Verifica se todas as tasks de entrega estão concluídas (conta a atual como concluída)
          const tasksEntrega = allTasks.filter(t => t.status === 'aguardando_aprovacao_entrega' || (t.aprovacaoEntrega === true && t.fase === 'execucao'));
          const todasConcluidas = tasksEntrega.every(t => t.id === task.id ? true : t.status === 'concluido');

          if (todasConcluidas) {
            // Monta relatório com todos os serviços executados
            const tasksExec = allTasks.filter(t => t.fase === 'execucao' || t.fase === 'preparacao');
            const relatorioItens = tasksExec.map(t => ({
              serviceName:          t.serviceName || '',
              serviceParentName:    t.serviceParentName || '',
              supplierName:         t.supplierName || '',
              fase:                 t.fase || '',
              status:               t.status || '',
              dataInicio:           t.dataInicio || '',
              dataEntrega:          t.dataEntrega || '',
              valor:                t.valor || 0,
              unidade:              t.unidade || '',
              observacaoFornecedor: t.observacaoFornecedor || '',
              aprovacaoObs:         t.aprovacaoObs || '',
            }));

            await updateDoc(doc(db, 'budgets', task.budgetId), {
              status:         'completed',
              workspaceStage: 'Concluido',
              concluidoEm:    serverTimestamp(),
              relatorioFinal: {
                geradoEm:      new Date().toISOString(),
                itens:         relatorioItens,
                totalServicos: relatorioItens.length,
              },
              updatedAt: serverTimestamp(),
            });
          }
        } catch (e) { console.error('Erro ao verificar conclusao do budget:', e); }
      }

      // Se aprovada e era task de PREPARAÇÃO → cria task de EXECUÇÃO
      if (aprovado && task.fase === 'preparacao') {
        await addDoc(collection(db, 'tasks'), {
          budgetId:          task.budgetId,
          supplierJobId:     task.supplierJobId,
          supplierId:        task.supplierId,
          supplierName:      task.supplierName,
          serviceName:       task.serviceName,
          serviceParentName: task.serviceParentName,
          tipoServico:       task.tipoServico,
          nome:              `Execução — ${task.serviceName}`,
          descricao:         task.descricao || '',
          dataInicio:        task.dataInicio || '',
          dataEntrega:       task.dataEntrega || '',
          diasAntes:         task.diasAntes || 0,
          diasPreparo:       task.diasPreparo || 0,
          diasMontagem:      task.diasMontagem || 0,
          diasEvento:        task.diasEvento || 1,
          valor:             task.valor || 0,
          preco:             task.preco || 0,
          unidade:           task.unidade || '',
          fase:              'execucao',
          status:            'pendente',
          cor:               '#00E5C4',
          preAprovacao:      false,
          aprovacaoExecucao: task.aprovacaoExecucao || false,
          taskPreparacaoId:  task.id,
          createdAt:         serverTimestamp(),
        });
      }
    } catch (e) { console.error(e); alert('Erro ao processar aprovação.'); }
    finally { setAprovandoTask(false); }
  };

  // Abre página do projeto ao clicar no card
  if (selectedEvent) {
    return (
      <ClienteProjetoScreen
        budget={selectedEvent}
        userData={userData}
        onBack={() => setSelectedEvent(null)}
      />
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0D1B2A', display: 'flex', fontFamily: 'Outfit, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .cl-sidebar { width: 220px; background: rgba(10,22,38,0.95); border-right: 1px solid rgba(0,180,255,0.08); display: flex; flex-direction: column; position: fixed; top: 0; bottom: 0; left: 0; z-index: 10; }
        .cl-logo { padding: 24px 20px 20px; border-bottom: 1px solid rgba(0,180,255,0.08); font-size: 18px; font-weight: 300; color: #E8F4FF; letter-spacing: 3px; }
        .cl-logo span { color: #00E5C4; font-weight: 500; }
        .cl-nav { flex: 1; padding: 16px 10px; display: flex; flex-direction: column; gap: 2px; }
        .cl-nav-item { background: none; border: none; color: #7BAFD4; padding: 10px 14px; text-align: left; font-size: 13px; font-weight: 300; cursor: pointer; border-radius: 8px; width: 100%; font-family: 'Outfit', sans-serif; transition: all 0.15s; }
        .cl-nav-item:hover { background: rgba(0,229,196,0.06); color: #E8F4FF; }
        .cl-nav-item.active { background: rgba(0,229,196,0.1); color: #00E5C4; }
        .cl-footer { padding: 16px; border-top: 1px solid rgba(0,180,255,0.08); }
        .cl-avatar { width: 32px; height: 32px; border-radius: 50%; background: rgba(0,229,196,0.15); border: 1.5px solid rgba(0,229,196,0.4); display: flex; align-items: center; justify-content: center; font-size: 12px; color: #00E5C4; font-weight: 600; }
        .cl-main { margin-left: 220px; flex: 1; padding: 28px 32px; }
        .cl-events-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; margin-top: 24px; }
        .cl-event-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(0,180,255,0.1); border-radius: 14px; padding: 20px; cursor: pointer; transition: all 0.2s; position: relative; overflow: hidden; }
        .cl-event-card:hover { background: rgba(0,229,196,0.04); border-color: rgba(0,229,196,0.25); transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.2); }
        .cl-event-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; }
        .cl-new-btn { display: flex; align-items: center; gap: 8px; padding: '12px 20px'; border-radius: 10px; border: '1px dashed rgba(0,229,196,0.3)'; background: 'rgba(0,229,196,0.04)'; color: '#00E5C4'; cursor: pointer; font-family: 'Outfit', sans-serif; font-size: 14px; transition: all 0.15s; }
      `}</style>

      {/* Sidebar */}
      <aside className="cl-sidebar">
        <div className="cl-logo" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {tenantData?.logo ? (
            <>
              <img src={tenantData.logo} alt={tenantData.nome || ''} style={{ height: 48, objectFit: 'contain', display: 'block', marginBottom: 8 }} />
              <div style={{ fontSize: 11, color: 'rgba(232,244,255,0.55)', letterSpacing: 1.5 }}>REALIZE<span style={{ color: '#00E5C4', fontWeight: 600 }}>HUB</span></div>
            </>
          ) : (
            <>realize<span>hub</span></>
          )}
        </div>
        <nav className="cl-nav">
          {pode('meus_eventos', 'V') && <button className={`cl-nav-item ${activeSection === 'workspace' ? 'active' : ''}`} onClick={() => setActiveSection('workspace')}>Workspace</button>}
          {pode('historico', 'V') && <button className={`cl-nav-item ${activeSection === 'historico' ? 'active' : ''}`} onClick={() => setActiveSection('historico')}>Histórico</button>}
          {pode('financeiro', 'V') && <button className={`cl-nav-item ${activeSection === 'financeiro' ? 'active' : ''}`} onClick={() => setActiveSection('financeiro')}>Financeiro</button>}
          {pode('agenda', 'V') && <button className={`cl-nav-item ${activeSection === 'agenda' ? 'active' : ''}`} onClick={() => setActiveSection('agenda')}>Agenda</button>}
        </nav>
        <div className="cl-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div className="cl-avatar">{userInitials}</div>
            <div>
              <div style={{ fontSize: 13, color: '#E8F4FF', fontWeight: 400 }}>{userName.split(' ')[0]}</div>
              <div style={{ fontSize: 11, color: 'rgba(123,175,212,0.5)' }}>Cliente</div>
            </div>
          </div>
          <button onClick={onLogout} style={{ width: '100%', padding: 9, background: 'none', border: '1px solid rgba(231,76,60,0.3)', borderRadius: 8, color: 'rgba(231,76,60,0.7)', fontSize: 12, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}>Sair</button>
        </div>
      </aside>

      {/* Main */}
      <main className="cl-main">
        {/* Header de boas-vindas */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid rgba(0,180,255,0.08)', marginBottom: 24 }}>
          <div />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 600, color: '#E8F4FF' }}>{userName}</div>
            <div style={{ fontSize: 13, color: '#7BAFD4', marginTop: 2 }}>
              {(tenantData?.nome || userData?.companyName)}{(tenantData?.nome || userData?.companyName) && userData?.roleName ? ' · ' : ''}{userData?.roleName}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <SinoNotificacoes userId={userId} tema="escuro" userData={userData} />
          </div>
        </div>

        {activeSection === 'workspace' && (
          <div style={{ background: '#ccd4ea', margin: '0 -32px -28px', padding: '24px 32px', minHeight: 'calc(100vh - 115px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 }}>
              <div>
                <h1 style={{ fontSize: 22, fontWeight: 300, color: '#1e293b', letterSpacing: -0.3 }}>Meus Eventos</h1>
                <p style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>Acompanhe seus eventos em tempo real</p>
              </div>
              <button
                style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid rgba(0,229,196,0.4)', background: 'rgba(0,229,196,0.1)', color: '#0d9488', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Outfit, sans-serif', transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,229,196,0.18)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,229,196,0.1)'}
                onClick={() => { setChatKey(k => k + 1); setShowChat(true); }}>
                + Novo Evento
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#475569', fontSize: 14 }}>Carregando...</div>
            ) : eventosAtivos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60 }}>
                <div style={{ fontSize: 14, color: '#475569', marginBottom: 20 }}>Você ainda não tem eventos</div>
                <button
                  onClick={() => { setChatKey(k => k + 1); setShowChat(true); }}
                  style={{ padding: '12px 28px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#00E5C4,#0080FF)', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}>
                  Planejar meu primeiro evento
                </button>
              </div>
            ) : (
              eventosAtivos.map(event => {
                const st = STATUS_CONFIG[event.status] || STATUS_CONFIG.analyzing;
                const temAcaoPendente = event.status === 'pendingApproval' || tasksPendentesAprov.some(t => t.budgetId === event.id);
                return (
                  <div key={event.id} onClick={() => setSelectedEvent(event)}
                    style={{ position: 'relative', display: 'flex', alignItems: 'center', background: '#e3eafa', borderRadius: 12, padding: '14px 16px', marginBottom: 10, cursor: 'pointer' }}>
                    {temAcaoPendente && (
                      <span title="Ação pendente" style={{ position: 'absolute', top: 10, right: 10, width: 9, height: 9, borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,0.2)' }} />
                    )}

                    {/* Info à esquerda */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 3 }}>
                        {event.eventName || event.eventTypeName || 'Meu Evento'}
                      </div>
                      {event.jobCode && <div style={{ fontSize: 11, fontWeight: 600, color: '#334155', marginBottom: 2 }}>{event.jobCode}</div>}
                      {event.eventTypeName && <div style={{ fontSize: 12, color: '#475569', marginBottom: 1 }}>{event.eventTypeName}</div>}
                      {(event.startDate || event.endDate) && (
                        <div style={{ fontSize: 12, color: '#475569' }}>
                          {event.startDate}{event.endDate && event.endDate !== event.startDate ? ` até ${event.endDate}` : ''}
                        </div>
                      )}
                    </div>

                    {/* Divisor */}
                    <div style={{ width: 1, height: 44, background: 'rgba(61,76,107,0.15)', margin: '0 16px' }} />

                    {/* Status à direita */}
                    <div style={{ alignItems: 'center', minWidth: 100, textAlign: 'center' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#7481a3', letterSpacing: 0.5, marginBottom: 6 }}>STATUS</div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 10, background: st.color, color: 'white' }}>{st.label}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeSection === 'historico' && (
          <div style={{ background: '#ccd4ea', margin: '0 -32px -28px', padding: '24px 32px', minHeight: 'calc(100vh - 115px)' }}>
            <div style={{ marginBottom: 20 }}>
              <h1 style={{ fontSize: 22, fontWeight: 300, color: '#1e293b', letterSpacing: -0.3 }}>Histórico</h1>
              <p style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>Eventos concluídos ou cancelados</p>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#475569', fontSize: 14 }}>Carregando...</div>
            ) : eventosHistorico.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60 }}>
                <div style={{ fontSize: 14, color: '#475569' }}>Nenhum evento no histórico ainda.</div>
              </div>
            ) : (
              eventosHistorico.map(event => {
                const st = STATUS_CONFIG[event.status] || STATUS_CONFIG.analyzing;
                return (
                  <div key={event.id} onClick={() => setSelectedEvent(event)}
                    style={{ position: 'relative', display: 'flex', alignItems: 'center', background: '#e3eafa', borderRadius: 12, padding: '14px 16px', marginBottom: 10, cursor: 'pointer' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 3 }}>
                        {event.eventName || event.eventTypeName || 'Meu Evento'}
                      </div>
                      {event.jobCode && <div style={{ fontSize: 11, fontWeight: 600, color: '#334155', marginBottom: 2 }}>{event.jobCode}</div>}
                      {event.eventTypeName && <div style={{ fontSize: 12, color: '#475569', marginBottom: 1 }}>{event.eventTypeName}</div>}
                      {(event.startDate || event.endDate) && (
                        <div style={{ fontSize: 12, color: '#475569' }}>
                          {event.startDate}{event.endDate && event.endDate !== event.startDate ? ` até ${event.endDate}` : ''}
                        </div>
                      )}
                    </div>
                    <div style={{ width: 1, height: 44, background: 'rgba(61,76,107,0.15)', margin: '0 16px' }} />
                    <div style={{ alignItems: 'center', minWidth: 100, textAlign: 'center' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#7481a3', letterSpacing: 0.5, marginBottom: 6 }}>STATUS</div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 10, background: st.color, color: 'white' }}>{st.label}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeSection === 'financeiro' && (
          <>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 300, color: '#E8F4FF', letterSpacing: -0.3 }}>Financeiro</h1>
              <p style={{ fontSize: 13, color: '#7BAFD4', marginTop: 4 }}>Sua verba disponível e o custo dos seus eventos</p>
            </div>

            {/* Carteira / verba disponível */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,180,255,0.1)', borderRadius: 14, padding: '20px 24px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(123,175,212,0.6)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Sua verba</div>
              {verbaBucket?.periodoUso && (
                <div style={{ fontSize: 12, color: '#7BAFD4', marginBottom: 16 }}>Período de uso: {verbaBucket.periodoUso}</div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(123,175,212,0.6)', marginBottom: 4 }}>Total</div>
                  <div style={{ fontSize: 18, fontWeight: 500, color: '#E8F4FF' }}>{formatBRL(saldoVerba)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(123,175,212,0.6)', marginBottom: 4 }}>Alocado (em eventos)</div>
                  <div style={{ fontSize: 18, fontWeight: 500, color: '#FFA726' }}>{formatBRL(valorAlocado)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(123,175,212,0.6)', marginBottom: 4 }}>Utilizado (pago)</div>
                  <div style={{ fontSize: 18, fontWeight: 500, color: '#ef4444' }}>{formatBRL(valorUtilizado)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(123,175,212,0.6)', marginBottom: 4 }}>Livre</div>
                  <div style={{ fontSize: 18, fontWeight: 500, color: '#00E5C4' }}>{formatBRL(valorLivre)}</div>
                </div>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.06)', overflow: 'hidden', display: 'flex' }}>
                <div style={{ width: `${pctUtilizado}%`, background: '#ef4444' }} />
                <div style={{ width: `${pctAlocado}%`, background: '#FFA726' }} />
              </div>
            </div>

            {/* Custos por evento */}
            <div style={{ background: '#ccd4ea', margin: '20px -32px -28px', padding: '20px 32px', minHeight: 'calc(100vh - 380px)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7481a3', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>Seus eventos</div>
              {events.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#475569', fontSize: 13 }}>Nenhum evento ainda.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {events.map(ev => {
                    const custo = ev.orcamentoFinal?.total || ev.financeiro?.valorTotal || 0;
                    const parcelas = ev.financeiro?.parcelas || [];
                    const notaEnviada = parcelas.length > 0 && parcelas.some(p => p.notaEnviada);
                    const pago = parcelas.length > 0 && parcelas.every(p => p.pago);
                    return (
                      <div key={ev.id} onClick={() => setSelectedEvent(ev)}
                        style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px', borderRadius: 12, background: '#e3eafa', cursor: 'pointer' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.eventName || ev.eventTypeName || 'Meu Evento'}</div>
                          <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{custo > 0 ? formatBRL(custo) : 'Sem custo definido ainda'}</div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 10, background: notaEnviada ? 'rgba(0,128,255,0.15)' : 'rgba(61,76,107,0.1)', color: notaEnviada ? '#0080FF' : '#7481a3' }}>
                          {notaEnviada ? 'Nota enviada' : 'Sem nota'}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 10, background: pago ? 'rgba(102,187,106,0.18)' : 'rgba(255,167,38,0.2)', color: pago ? '#2e7d32' : '#b45309' }}>
                          {pago ? '✓ Pago' : 'Pendente'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {activeSection === 'agenda' && (
          <div style={{ color: '#7BAFD4', fontSize: 14, paddingTop: 40, textAlign: 'center' }}>
            Agenda em breve
          </div>
        )}
      </main>



      {/* Modal do Chat com a Bia */}
      {showChat && (
        <ClienteChatV4
          key={chatKey}
          userData={userData}
          onClose={() => setShowChat(false)}
          tenant={tenant}
        />
      )}
    </div>
  );
}
